/**
 * report-step.service.ts
 * Copyright © 2016-2018, My Coverage Plan, Inc.
 * All rights reserved.
 */

/**
 * @author Todd L Smith <tsmith@safetyweb.org>
 */

import { Inject, Injectable } from "@angular/core";
import { Observable } from "rxjs/Rx";

import { AlgorithmStep, ReportStep } from "../structure";
import { AlgorithmService } from "./algorithm.service";

import { StepTransformer } from "./pipeline/step-transformer";
import { GlobalDeactivation, StepUpdate } from "./pipeline/step-update";
import { TransformerService }
  from "./pipeline/transformer.service";
import { StepFacade } from "./step.facade";

import { AlertService } from "../../common/alert.service";
import { MessageReceiver } from "../../server/message-receiver";
import { MessageTag } from "../../server/messages/";
import { NextStepsResponse } from "../../server/messages/algorithms/";
import { MessagingService } from "../../server/messaging.service";
import { Modal } from "angular2-modal/plugins/bootstrap";
import { ModalHelpers } from "app/support/utilities";
import { ReportDetailsModalComponent } from "app/intake-form/report-details-modal.component";
import { last } from "@angular/router/src/utils/collection";
import { ModalService } from "app/common/modal.service";

/**
 * Number of milliseconds after which a series of report activeness transitions
 * are considered to be "settled", for determining whether to actually trigger
 * UI transitions and notifications and avoid blink.
 * @devConfig
 */
const DEACTIVATION_SETTLED_TIME : number = 2000;

const NOTIFICATION_AGGLUTINATION_TIME : number = 1000;

/**
 * A {@link ReportStepService} interacts with the server to negotiate
 * {@link ReportStep}s. It receives notifications about new {@link ReportStep}s.
 */
@Injectable()
export class ReportStepService extends StepFacade<ReportStep>
  implements MessageReceiver
{
  /**
   * Construct a new {@link ReportStepService}.
   *
   * @param messaging
   *        The active {@link MessagingService}.
   */
  public constructor (
    protected messaging : MessagingService,
    protected alerts : AlertService,
    protected algorithms : AlgorithmService,
    protected transformer : TransformerService,
    protected modals : ModalService
  )
  {
    super(messaging, algorithms);
  }

  /**
   * Whether this service can be used to process an {@link AlgorithmStep}.
   * (This method is mostly used as a TypeScript predicate.)
   * @param step A step instance to check
   */
  protected matchesSubtype (step : AlgorithmStep) : step is ReportStep
  {
    return (step instanceof ReportStep);
  }

  /**
   * Retrieve all of the defined {@link ReportStep}s.
   */
  public all () : ReportStep[]
  {
    if (!this.isRunning())
    {
      return [];
    }
    return this.algorithms.running.steps.reports();
  }

  /**
   * Shortcut to retrieve all of the active {@link ReportStep}s.
   */
  public active () : ReportStep[]
  {
    return this.all().filter((step : ReportStep) => step.isActive);
  }

  public updates () : Observable<StepUpdate<any, ReportStep>>
  {
    this.assertRunning();
    return this.algorithms.running.steps.updates.filter(
      (update : StepUpdate<any, any>) => update.matchesType(ReportStep));
  }

  /**
   * A record of the last moment each currently-inactive step was active.
   */
  public lastActive : Map<number, number> = new Map<number, number>();

  /**
   * A record of the last time ALL steps were deactivated
   */
  public lastGlobalDeactivate : number = 0;
  
  public lastReportNotification : number = 0;

  public lastReportCount : number = 0;

  /**
   * Called when the {@link MessagingService} is ready. Listen for reports from
   * the server, toggle their statuses, and alert the user to important changes.
   */
  public connected () : void
  {
    this.subscriptions = this.messaging.receive(MessageTag.RSP_NEXT_STEPS)
      .flatMap((m : NextStepsResponse) => m.reportSteps)
      .map((s : ReportStep) => {
        s = new ReportStep(s);
        this.transformer.run(s, true);
        return s;
      })
      .filter((s : ReportStep) => s.hints.visible !== false)
      .subscribe(
        // tslint:disable-next-line:cyclomatic-complexity
        (step : ReportStep) => {
        const previous : ReportStep =
          this.algorithms.running.steps.get(step.id) as ReportStep;
        if (step.isActive)
        {
          // We only want to generate a notification about a (potentially-new)
          // report definition iff:
          //  1. The new definition is active
          //  2. The step was either unknown or previously inactive
          //  3. If previously inactive, it was either never active before or
          //     the time since it was last active is greater than some
          //     threshold (on the order of seconds, not millis)
          //  4. The algorithm was not launched "recently" (see inner cond.)
          //
          // This convoluted logic is all to prevent "false positive" alerts
          // when the server quickly toggles activation due to cascades,
          // restarts, or user conflicts.
          const lastActive : number =
            this.lastActive.get(step.id) || this.lastGlobalDeactivate;
          if (step.id === 90)
          {
            console.warn(Date.now());
            console.warn(lastActive);
            console.warn(Date.now() - lastActive);
            // debugger;
          }
          if (
            (!previous ||
                (!previous.isActive &&
                  (Date.now() - lastActive) > DEACTIVATION_SETTLED_TIME)
            )
          )
          {
            if (!this.algorithms.running.recentlyLaunched)
            {
              // Additionally, if the last notification about a report was
              // recent merge the notifications into a single message about the
              // overall quantity of reports being activated.
              if ((Date.now() - this.lastReportNotification) <
                  NOTIFICATION_AGGLUTINATION_TIME)
              {
                this.lastReportCount++;
                this.alerts.addAndReplace(
                  `We have found ${this.lastReportCount} new recommendations!`,
                  "notification",
                  "new report");
                // this.alerts.addAndReplace(
                //   `You have activated ${this.lastReportCount} new reports!`,
                //   "notification",
                //   "new report");
              }
              else
              {
                this.lastReportNotification = Date.now();
                this.lastReportCount = 1;
                this.alerts.addAndReplace(
                  `We found a recommendation for you! ("${step.displayName}")`,
                  "notification",
                  "new report");
                // this.alerts.addAndReplace(
                //   `You have activated the "${step.displayName}" report!`,
                //   "notification",
                //   "new report");
              }
            }
            // If the report is hinted to behave as an alert, generate a modal
            // for it.
            if (step.hints.alert === true)
            {
              this.modals.display(
                ReportDetailsModalComponent,
                {
                  selected: step,
                  explanation: Observable.empty(),
                  important: true
                }
              );
            }
          }
        }
        else if (previous && previous.isActive)
        {
          // If it WAS active and is now being deactivated, record this moment
          // as the last time it was active (for use in the above logic later).
          this.lastActive.set(step.id, Date.now());
        }

        // Formally define the new step on the StepManager, which will both
        // record it in the global map and issue a definition event.
        this.algorithms.running.steps.define(step);
      }
    );

    // In addition to the activation record-keeping above, we must also listen
    // for global deactivation events and record when they occur. This is
    // especially important because algorithm restart events are one of the
    // primary causes of false-positive new report notifications.
    this.subscriptions.add(
      this.algorithms.smoothUpdates.filter(
        (update : StepUpdate<any, any>) => update instanceof GlobalDeactivation
      ).subscribe(() => {
        this.lastGlobalDeactivate = Date.now();
        console.log("global deactivate", this.lastGlobalDeactivate);
        // this.lastActive.clear();
      })
    );
  }

  /**
   * Teardown instructions for when the {@link MessagingService} has stopped.
   */
  public disconnected () : void {/* decorator-generated */}
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/algorithms/management/report-step.service.ts